import {__} from '@wordpress/i18n';
import {registerPaymentMethod} from '@woocommerce/blocks-registry';
import {getSetting} from '@woocommerce/settings';
import {decodeEntities} from '@wordpress/html-entities';
import {useEffect, useRef} from '@wordpress/element';
import {type PaymentMethodInterface} from '@woocommerce/types';
import {type CheckoutAfterProcessingWithErrorEventData} from '@woocommerce/checkout';
import {type TokenResult, type BillingContact, type Card, type ChargeVerifyBuyerDetails, type Payments, type StoreVerifyBuyerDetails, type VerifyBuyerResponseDetails} from '@square/web-payments-sdk-types';
import {startTransaction, updateTransaction, z} from '@peachpay/checkout-blocks';
import {getErrorString} from '../../../../../../frontend/@shared/ts/error';
import CurrencyFallback from '../../../../../blocks/src/currency-fallback';

const SettingsSchema = z.object({
	icon: z.string(),
	title: z.string(),
	description: z.string(),
	fallback_currency: z.string().nullable(),
	supports: z.string().array(),
	square: z.object({
		application_id: z.string(),
		location_id: z.string(),
	}),
});

const parsedResult = SettingsSchema.safeParse(getSetting('peachpay_square_card_data'));
if (!parsedResult.success) {
	throw new Error(`Payment method "peachpay_square_card" setup aborted. Invalid settings.\n\n${parsedResult.error.message}`);
}

const settings = parsedResult.data;
const title = decodeEntities(settings.title);
const description = decodeEntities(settings.description);

if (!window.Square) {
	throw new Error('Payment method "peachpay_square_card" setup aborted. Square SDK not loaded.');
}

let payments: Payments;
try {
	payments = window.Square.payments(settings.square.application_id, settings.square.location_id);
} catch (err) {
	throw new Error(`Payment method "peachpay_square_card" setup aborted. Could not create Payments instance.\n\n${getErrorString(err)}`);
}

/**
 * Component to render the payment method label
 */
function Label(props: PaymentMethodInterface) {
	const {PaymentMethodLabel, PaymentMethodIcons} = props.components;
	return (
		<>
			<PaymentMethodLabel text={title} />
			<PaymentMethodIcons className='pp-label-icon' icons={[{
				id: 'peachpay_square_card',
				src: settings.icon,
				alt: title,
			}]} />
		</>
	);
}

/**
 * Component to render the payment method card fields
 */
function Content({billing, eventRegistration, emitResponse}: PaymentMethodInterface) {
	const {
		onPaymentSetup,
		onCheckoutFail,
	} = eventRegistration;

	const cardRef = useRef<Card | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	/**
	 * Setup the Square card element and attach it to this react component.
	 */
	useEffect(() => {
		let resize: ResizeObserver;
		const setupCard = async () => {
			if (containerRef.current === null) {
				return;
			}

			cardRef.current = await payments.card();

			// The Square card element does not automatically resize. This runs
			// whenever the container resizes to recalculate the card element size.
			const resize = new ResizeObserver(() => {
				cardRef.current?.recalculateSize();
			});
			resize.observe(containerRef.current);

			await cardRef.current.attach(containerRef.current);
			// Remove the loading UI
			containerRef.current.querySelector('.pp-loading-blur')?.remove();
		};

		setupCard().catch(console.error);

		return () => {
			resize?.disconnect();
			cardRef.current?.detach()
				.then(() => cardRef.current?.destroy())
				.catch(console.error);
		};
	}, []);

	/**
	 * Handle payment tokenization and verification right before the order is submitted.
	 */
	useEffect(() => onPaymentSetup(async () => {
		const {result: transactionId, error} = await startTransaction('peachpay_square_card');
		if (error || !transactionId) {
			return {
				type: emitResponse.responseTypes.ERROR,
				message: getErrorString(error) ?? __('An unknown error occured while creating the transaction.', 'peachpay-for-woocommerce'),
			};
		}

		if (!cardRef.current) {
			const note = __('Missing ref to Square card element. Please try again or use a different payment method.', 'peachpay-for-woocommerce');

			await updateTransaction(transactionId, {note, paymentStatus: 'creationfailed'});
			return {
				type: emitResponse.responseTypes.ERROR,
				message: note,
			};
		}

		let tokenResult: TokenResult | null = null;
		try {
			tokenResult = await cardRef.current.tokenize();
			if (tokenResult.status !== 'OK' || !tokenResult.token) {
				const note = (tokenResult.errors
					?.map(err => err.message)
					?.join('<br/>')
						?? __('Card tokenization failed. Please try again or use a different payment method.', 'peachpay-for-woocommerce')
				);

				await updateTransaction(transactionId, {note, paymentStatus: 'creationfailed'});
				return {
					type: emitResponse.responseTypes.ERROR,
					message: note,
				};
			}
		} catch (err) {
			const note = getErrorString(err);
			await updateTransaction(transactionId, {note, paymentStatus: 'creationfailed'});
			return {
				type: emitResponse.responseTypes.ERROR,
				message: note,
			};
		}

		let verificationResult: VerifyBuyerResponseDetails | null = null;
		try {
			const buyerDetails = () => {
				const billingContact = {
					addressLines: [
						billing.billingAddress.address_1,
						billing.billingAddress.address_2,
					],
					city: billing.billingAddress.city,
					countryCode: billing.billingAddress.country,
					email: billing.billingAddress.email,
					familyName: billing.billingAddress.last_name,
					givenName: billing.billingAddress.first_name,
					phone: billing.billingAddress.phone,
					state: billing.billingAddress.state,
					postalCode: billing.billingAddress.postcode,
				} satisfies BillingContact;

				if (billing.cartTotal.value > 0) {
					return {
						intent: 'CHARGE',
						amount: String(billing.cartTotal.value),
						currencyCode: billing.currency.code,
						billingContact,
					} satisfies ChargeVerifyBuyerDetails;
				}

				return {
					intent: 'STORE',
					billingContact,
				} satisfies StoreVerifyBuyerDetails;
			};

			verificationResult = await payments.verifyBuyer(tokenResult.token, buyerDetails());
			if (!verificationResult) {
				const note = __('Card verification failed. Please try again or use a different payment method.', 'peachpay-for-woocommerce');
				await updateTransaction(transactionId, {note, paymentStatus: 'creationfailed'});

				return {
					type: emitResponse.responseTypes.ERROR,
					message: note,
				};
			}
		} catch (err) {
			let note = getErrorString(err);

			if (err instanceof Error && err.name === 'VerifyBuyerError') {
				note = __('Card verification failed or was canceled by the buyer.', 'peachpay-for-woocommerce');
			}

			await updateTransaction(transactionId, {note, paymentStatus: 'creationfailed'});
			return {
				type: emitResponse.responseTypes.ERROR,
				message: note,
			};
		}

		return {
			type: emitResponse.responseTypes.SUCCESS,
			meta: {
				paymentMethodData: {
					peachpay_transaction_id: transactionId,
					peachpay_square_source_id: tokenResult.token,
					peachpay_square_verification_id: verificationResult.token,
				},
			},
		};
	}), [
		billing.billingAddress,
		billing.cartTotal.value,
		billing.currency.code,
		onPaymentSetup,
	]);

	/**
	 * Handle checkout failure after the order was submitted. Failure may or may
	 * not be payment related.
	 */
	useEffect(() => onCheckoutFail(async (data: CheckoutAfterProcessingWithErrorEventData) => ({
		type: emitResponse.responseTypes.ERROR,
		message: (data.processingResponse?.paymentDetails?.['message']) ?? __('An unknown error occured while processing the payment.', 'peachpay-for-woocommerce'),
		messageContext: emitResponse.noticeContexts.PAYMENTS,
	})), [onCheckoutFail]);

	/**
	 * If the payment method does not support the current currency we will show
	 * a fallback button to allow changing the currency.
	 *
	 * Note: Due to how our currency switcher works the `fallback_currency` setting
	 * is only defined when the payment does not support the current currency and
	 * does not change after page load.
	 */
	if (settings.fallback_currency) {
		return <CurrencyFallback name={title} currency={settings.fallback_currency} />;
	}

	return (
		<>
			<p>{description}</p>
			<div ref={containerRef}>
				<div className='pp-loading-blur' style={{height: '5rem'}}></div>
			</div>
		</>
	);
}

/**
 * Component to handle saved payment method submissions.
 */
const SavedMethod = ({emitResponse, eventRegistration, token}: PaymentMethodInterface & {token: string}) => {
	const {
		onPaymentSetup,
	} = eventRegistration;

	/**
	 * Handle setting up a payment using a saved token before the order is submitted.
	 */
	useEffect(() => onPaymentSetup(async () => {
		const {result: transactionId, error} = await startTransaction('peachpay_square_card');
		if (error || !transactionId) {
			return {
				type: emitResponse.responseTypes.ERROR,
				message: getErrorString(error),
			};
		}

		return {
			type: emitResponse.responseTypes.SUCCESS,
			meta: {
				paymentMethodData: {
					peachpay_transaction_id: transactionId,
					'wc-peachpay_square_card-payment-token': token,
				},
			},

		};
	}), [onPaymentSetup]);
};

registerPaymentMethod({
	name: 'peachpay_square_card',
	ariaLabel: title,
	canMakePayment(data) {
		return data.paymentMethods.includes('peachpay_square_card');
	},
	// @ts-expect-error WC will clone these components and pass the props when used.
	label: <Label />,
	// @ts-expect-error WC will clone these components and pass the props when used.
	content: <Content />,
	// @ts-expect-error WC will clone these components and pass the props when used.
	savedTokenComponent: <SavedMethod />,
	// @ts-expect-error WC will clone these components and pass the props when used.
	edit: <Content />,
	supports: {
		features: settings.supports,
		showSavedCards: !settings.fallback_currency,
		showSaveOption: !settings.fallback_currency,
	},
});
